<template>
  <div class="knowledge-graph-editor">
    <div class="editor-header">
      <h2>知识点关系图编辑器</h2>
      <div class="header-actions">
        <button class="btn-primary" @click="saveGraph">保存关系图</button>
        <button class="btn-secondary" @click="clearGraph">清空画板</button>
        <button class="btn-secondary" @click="showJsonModal = true">查看JSON</button>
      </div>
    </div>

    <div class="editor-container">
      <!-- 知识点列表 -->
      <div class="knowledge-points-panel">
        <h3>知识点列表</h3>
        <div class="search-box">
          <input 
            type="text" 
            v-model="searchKeyword" 
            placeholder="查找知识点..."
            @input="filterKnowledgePoints"
          >
        </div>
        <div class="points-list">
          <div 
            v-for="point in filteredKnowledgePoints" 
            :key="point.kpId"
            class="knowledge-point-item"
            draggable="true"
            @dragstart="onDragStart($event, point)"
          >
            <div class="point-circle" :style="{ backgroundColor: getPointColor(point.category) }">
              {{ point.name.charAt(0) }}
            </div>
            <div class="point-info">
              <div class="point-name">{{ point.name }}</div>
              <div class="point-category">{{ point.category }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 画板区域 -->
      <div class="canvas-container">
        <div class="canvas-header">
          <h3>关系图画板</h3>
          <div class="canvas-tools">
            <button class="btn-tool" @click="toggleConnectionMode">
              {{ connectionMode ? '退出连接模式' : '连接模式' }}
            </button>
            <button class="btn-tool" @click="deleteSelected">删除选中</button>
          </div>
        </div>
        
        <div 
          class="canvas" 
          ref="canvas"
          @dragover="onDragOver"
          @drop="onDrop"
          @click="onCanvasClick"
        >
          <!-- 画布上的节点 -->
          <div 
            v-for="node in graphNodes" 
            :key="node.id"
            class="graph-node"
            :class="{ 
              'selected': selectedNode === node.id,
              'connecting': connectionMode && selectedNode === node.id 
            }"
            :style="{ 
              left: node.x + 'px', 
              top: node.y + 'px',
              backgroundColor: getPointColor(node.category)
            }"
            @mousedown="onNodeMouseDown($event, node)"
            @click.stop="onNodeClick(node)"
          >
            <div class="node-content">
              <div class="node-title">{{ node.name }}</div>
              <div class="node-category">{{ node.category }}</div>
            </div>
          </div>

          <!-- 连接线 -->
          <svg class="connections-svg" ref="connectionsSvg">
            <defs>
              <marker 
                id="arrowhead" 
                markerWidth="10" 
                markerHeight="7" 
                refX="9" 
                refY="3.5" 
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
              </marker>
            </defs>
            <g>
              <line 
                v-for="connection in connections" 
                :key="connection.id"
                :x1="connection.x1" 
                :y1="connection.y1" 
                :x2="connection.x2" 
                :y2="connection.y2"
                stroke="#666" 
                stroke-width="2"
                marker-end="url(#arrowhead)"
                class="connection-line"
              />
            </g>
          </svg>
        </div>
      </div>
    </div>

    <!-- JSON展示模态框 -->
    <div class="modal" v-if="showJsonModal">
      <div class="modal-content">
        <h3>关系图JSON数据</h3>
        <div class="json-container">
          <pre>{{ graphJson }}</pre>
        </div>
        <div class="modal-actions">
          <button class="btn-primary" @click="copyJson">复制JSON</button>
          <button class="btn-secondary" @click="showJsonModal = false">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KnowledgeGraphEditor',
  data() {
    return {
      knowledgePoints: [],
      filteredKnowledgePoints: [],
      searchKeyword: '',
      graphNodes: [],
      connections: [],
      selectedNode: null,
      connectionMode: false,
      showJsonModal: false,
      nextNodeId: 1,
      nextConnectionId: 1,
      isDragging: false,
      dragOffset: { x: 0, y: 0 }
    }
  },
  computed: {
    graphJson() {
      return JSON.stringify({
        nodes: this.graphNodes.map(node => ({
          id: node.id,
          kpId: node.kpId,
          name: node.name,
          category: node.category,
          x: node.x,
          y: node.y
        })),
        connections: this.connections.map(conn => ({
          id: conn.id,
          from: conn.from,
          to: conn.to
        }))
      }, null, 2)
    }
  },
  created() {
    //检查是否登录
    if (!this.$root.isLoggedIn) {
      this.$goToLogin(this.$route.fullPath)
      return
    }
    this.fetchKnowledgePoints()
  },
  mounted() {
    this.updateConnections()
  },
  methods: {
    async fetchKnowledgePoints() {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/points/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page: 1,
            pageSize: 1000,
            keyword: ''
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.knowledgePoints = data.data.list
          this.filteredKnowledgePoints = [...this.knowledgePoints]
        } else {
          this.$message.error(data.message || '获取知识点列表失败')
        }
      } catch (error) {
        console.error('Failed to fetch knowledge points:', error)
        this.$message.error('获取知识点列表失败')
      }
    },
    filterKnowledgePoints() {
      if (!this.searchKeyword.trim()) {
        this.filteredKnowledgePoints = [...this.knowledgePoints]
      } else {
        this.filteredKnowledgePoints = this.knowledgePoints.filter(point =>
          point.name.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
          point.category.toLowerCase().includes(this.searchKeyword.toLowerCase())
        )
      }
    },
    onDragStart(event, point) {
      event.dataTransfer.setData('text/plain', JSON.stringify(point))
    },
    onDragOver(event) {
      event.preventDefault()
    },
    onDrop(event) {
      event.preventDefault()
      const pointData = JSON.parse(event.dataTransfer.getData('text/plain'))
      const rect = this.$refs.canvas.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      
      this.addNode(pointData, x, y)
    },
    addNode(point, x, y) {
      const node = {
        id: this.nextNodeId++,
        kpId: point.kpId,
        name: point.name,
        category: point.category,
        x: x,
        y: y
      }
      this.graphNodes.push(node)
      this.updateConnections()
    },
    onNodeMouseDown(event, node) {
      if (this.connectionMode) {
        if (this.selectedNode === node.id) {
          this.selectedNode = null
        } else if (this.selectedNode) {
          // 创建连接
          this.createConnection(this.selectedNode, node.id)
          this.selectedNode = null
          this.connectionMode = false
        } else {
          this.selectedNode = node.id
        }
      } else {
        // 开始拖拽
        this.isDragging = true
        this.selectedNode = node.id
        const rect = event.target.getBoundingClientRect()
        this.dragOffset = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top
        }
        
        document.addEventListener('mousemove', this.onMouseMove)
        document.addEventListener('mouseup', this.onMouseUp)
      }
    },
    onMouseMove(event) {
      if (!this.isDragging) return
      
      const node = this.graphNodes.find(n => n.id === this.selectedNode)
      if (node) {
        const rect = this.$refs.canvas.getBoundingClientRect()
        node.x = event.clientX - rect.left - this.dragOffset.x
        node.y = event.clientY - rect.top - this.dragOffset.y
        this.updateConnections()
      }
    },
    onMouseUp() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onMouseMove)
      document.removeEventListener('mouseup', this.onMouseUp)
    },
    onNodeClick(node) {
      if (!this.connectionMode) {
        this.selectedNode = this.selectedNode === node.id ? null : node.id
      }
    },
    onCanvasClick() {
      if (!this.connectionMode) {
        this.selectedNode = null
      }
    },
    createConnection(fromNodeId, toNodeId) {
      // 检查是否已存在连接
      const existingConnection = this.connections.find(
        conn => conn.from === fromNodeId && conn.to === toNodeId
      )
      if (existingConnection) {
        this.$message.warning('连接已存在')
        return
      }
      
      const connection = {
        id: this.nextConnectionId++,
        from: fromNodeId,
        to: toNodeId
      }
      this.connections.push(connection)
      this.updateConnections()
    },
    updateConnections() {
      this.$nextTick(() => {
        this.connections.forEach(connection => {
          const fromNode = this.graphNodes.find(n => n.id === connection.from)
          const toNode = this.graphNodes.find(n => n.id === connection.to)
          
          if (fromNode && toNode) {
            connection.x1 = fromNode.x + 50
            connection.y1 = fromNode.y + 25
            connection.x2 = toNode.x
            connection.y2 = toNode.y + 25
          }
        })
      })
    },
    toggleConnectionMode() {
      this.connectionMode = !this.connectionMode
      if (!this.connectionMode) {
        this.selectedNode = null
      }
    },
    deleteSelected() {
      if (this.selectedNode) {
        // 删除节点
        this.graphNodes = this.graphNodes.filter(n => n.id !== this.selectedNode)
        // 删除相关连接
        this.connections = this.connections.filter(
          conn => conn.from !== this.selectedNode && conn.to !== this.selectedNode
        )
        this.selectedNode = null
        this.updateConnections()
      }
    },
    clearGraph() {
      if (confirm('确定要清空整个画板吗？')) {
        this.graphNodes = []
        this.connections = []
        this.selectedNode = null
        this.connectionMode = false
        this.nextNodeId = 1
        this.nextConnectionId = 1
      }
    },
    saveGraph() {
      const graphData = {
        nodes: this.graphNodes,
        connections: this.connections
      }
      
      // 这里可以添加保存到后端的逻辑
      console.log('保存的关系图数据:', graphData)
      this.$message.success('关系图已保存')
    },
    copyJson() {
      navigator.clipboard.writeText(this.graphJson).then(() => {
        this.$message.success('JSON已复制到剪贴板')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },
    getPointColor(category) {
      const colors = {
        '数学': '#FF6B6B',
        '物理': '#4ECDC4',
        '化学': '#45B7D1',
        '生物': '#96CEB4',
        '历史': '#FFEAA7',
        '地理': '#DDA0DD',
        '语文': '#98D8C8',
        '英语': '#F7DC6F',
        '政治': '#BB8FCE',
        '技术': '#85C1E9'
      }
      return colors[category] || '#95A5A6'
    }
  }
}
</script>

<style scoped>
.knowledge-graph-editor {
  padding: 20px;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.editor-container {
  display: flex;
  flex: 1;
  gap: 20px;
  min-height: 0;
}

.knowledge-points-panel {
  width: 300px;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

.knowledge-points-panel h3 {
  margin: 0;
  padding: 15px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
  border-radius: 8px 8px 0 0;
}

.search-box {
  padding: 15px;
  border-bottom: 1px solid #eee;
}

.search-box input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.points-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.knowledge-point-item {
  display: flex;
  align-items: center;
  padding: 10px;
  margin-bottom: 8px;
  border: 1px solid #eee;
  border-radius: 6px;
  cursor: grab;
  transition: all 0.2s;
}

.knowledge-point-item:hover {
  background-color: #f9f9f9;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.point-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  margin-right: 10px;
  flex-shrink: 0;
}

.point-info {
  flex: 1;
  min-width: 0;
}

.point-name {
  font-weight: 500;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.point-category {
  font-size: 12px;
  color: #666;
}

.canvas-container {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

.canvas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
  border-radius: 8px 8px 0 0;
}

.canvas-header h3 {
  margin: 0;
}

.canvas-tools {
  display: flex;
  gap: 10px;
}

.canvas {
  flex: 1;
  position: relative;
  background-color: #fafafa;
  overflow: hidden;
  cursor: crosshair;
}

.graph-node {
  position: absolute;
  width: 100px;
  height: 50px;
  border-radius: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  cursor: move;
  user-select: none;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.graph-node:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.graph-node.selected {
  border: 3px solid #2196F3;
  transform: scale(1.1);
}

.graph-node.connecting {
  border: 3px solid #FF9800;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(255, 152, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0); }
}

.node-content {
  text-align: center;
  font-size: 12px;
  line-height: 1.2;
}

.node-title {
  font-weight: bold;
  margin-bottom: 2px;
}

.node-category {
  font-size: 10px;
  opacity: 0.9;
}

.connections-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.connection-line {
  transition: stroke 0.2s;
}

.connection-line:hover {
  stroke: #2196F3;
  stroke-width: 3;
}

.btn-primary,
.btn-secondary,
.btn-tool {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #2196F3;
  color: white;
}

.btn-primary:hover {
  background-color: #1976D2;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

.btn-tool {
  background-color: #FF9800;
  color: white;
  font-size: 12px;
  padding: 6px 12px;
}

.btn-tool:hover {
  background-color: #F57C00;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 80%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
}

.json-container {
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  margin: 15px 0;
  max-height: 400px;
  overflow-y: auto;
}

.json-container pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
</style> 